import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  fetchArtifactContent,
  fetchArtifactsManifest,
} from "../../../../store/remediation/remediationSlice";
import { friendlyErrorMessage } from "../common/ErrorCodeMap";
import { formatAbsolute } from "../common/hooks";
import {
  formatArtifactBytes,
  getArtifactPhaseLabel,
  sortArtifactPhases,
} from "../common/artifactPhases";
import { FILTER_FONT_SIZE } from "../common/filterBarStyles";
import ArtifactContentView from "./ArtifactContentView";

/**
 * Lists artifact phases (GET …/artifacts) and loads JSON content per phase
 * (GET …/artifacts/{phase}).
 */
const ArtifactsPanel = ({ requestId, active }) => {
  const dispatch = useDispatch();
  const [selectedPhase, setSelectedPhase] = useState(null);

  const manifestSlice = useSelector((s) =>
    requestId != null ? s.remediation.artifactsManifestById[requestId] : null
  );
  const contentKey =
    requestId != null && selectedPhase ? `${requestId}:${selectedPhase}` : null;
  const contentSlice = useSelector((s) =>
    contentKey ? s.remediation.artifactContentByKey[contentKey] : null
  );

  const loadManifest = useCallback(() => {
    if (requestId == null) return;
    dispatch(fetchArtifactsManifest(requestId));
  }, [requestId, dispatch]);

  const loadSelectedPhaseContent = useCallback(() => {
    if (requestId == null || !selectedPhase) return;
    dispatch(fetchArtifactContent({ requestId, phase: selectedPhase }));
  }, [requestId, selectedPhase, dispatch]);

  const refreshAll = useCallback(() => {
    loadManifest();
    loadSelectedPhaseContent();
  }, [loadManifest, loadSelectedPhaseContent]);

  const selectPhase = useCallback((phase) => {
    if (requestId == null || !phase) return;
    setSelectedPhase(phase);
  }, [requestId]);

  useEffect(() => {
    setSelectedPhase(null);
  }, [requestId]);

  useEffect(() => {
    if (!active || requestId == null) return;
    loadManifest();
  }, [active, requestId, loadManifest]);

  const phases = useMemo(
    () => sortArtifactPhases(manifestSlice?.data?.phases || []),
    [manifestSlice?.data?.phases]
  );

  const selectedEntry = useMemo(
    () => phases.find((p) => p.phase === selectedPhase) || null,
    [phases, selectedPhase]
  );

  useEffect(() => {
    if (!active || !phases.length) {
      if (!phases.length) setSelectedPhase(null);
      return;
    }
    if (!selectedPhase || !phases.some((p) => p.phase === selectedPhase)) {
      setSelectedPhase(phases[0].phase);
    }
  }, [active, phases, selectedPhase]);

  useEffect(() => {
    if (!active || requestId == null || !selectedPhase) return;
    dispatch(fetchArtifactContent({ requestId, phase: selectedPhase }));
  }, [active, requestId, selectedPhase, dispatch]);

  const retrySelectedPhase = () => {
    loadSelectedPhaseContent();
  };

  const manifestLoading = manifestSlice?.status === "loading";
  const manifestFailed = manifestSlice?.status === "failed";
  const contentLoading = contentSlice?.status === "loading";
  const contentFailed = contentSlice?.status === "failed";
  const hasContent = contentSlice?.data != null;
  const isInitialLoad = contentLoading && !hasContent;

  if (requestId == null) {
    return (
      <Typography variant="caption" color="text.secondary">
        No request selected.
      </Typography>
    );
  }

  return (
    <Box>
      <StackHeader
        onRefresh={refreshAll}
        refreshing={manifestLoading || contentLoading}
      />

      {manifestFailed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {friendlyErrorMessage(manifestSlice.error)}
        </Alert>
      )}

      {manifestLoading && !manifestSlice?.data && (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={36} />
          <Skeleton variant="rounded" height={200} />
        </Stack>
      )}

      {!manifestLoading && !manifestFailed && phases.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: FILTER_FONT_SIZE }}>
          No artifacts have been captured for this request yet. Phases appear after preview or
          execution steps write to S3.
        </Typography>
      )}

      {phases.length > 0 && (
        <Box>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={0.75}
            sx={{ mb: 1.5 }}
            role="tablist"
            aria-label="Artifact phases"
          >
            {phases.map((entry) => {
              const isSelected = selectedPhase === entry.phase;
              return (
                <Chip
                  key={entry.phase}
                  role="tab"
                  aria-selected={isSelected}
                  label={getArtifactPhaseLabel(entry.phase)}
                  onClick={() => selectPhase(entry.phase)}
                  variant={isSelected ? "filled" : "outlined"}
                  color={isSelected ? "primary" : "default"}
                  sx={{
                    height: "auto",
                    py: 0.75,
                    "& .MuiChip-label": {
                      display: "block",
                      whiteSpace: "normal",
                      fontSize: 12,
                      fontWeight: isSelected ? 600 : 500,
                      lineHeight: 1.3,
                      px: 1.25,
                    },
                  }}
                />
              );
            })}
          </Stack>

          {selectedEntry && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5, fontSize: 11 }}
            >
              {formatArtifactBytes(selectedEntry.size_bytes)}
              {selectedEntry.captured_at
                ? ` · Captured ${formatAbsolute(selectedEntry.captured_at)}`
                : ""}
            </Typography>
          )}

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1.5,
              p: 2,
              backgroundColor: "grey.50",
            }}
          >
            {isInitialLoad && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                  Loading {getArtifactPhaseLabel(selectedPhase).toLowerCase()}…
                </Typography>
              </Box>
            )}

            {contentFailed && (
              <Alert
                severity="warning"
                sx={{ fontSize: 13, mb: hasContent ? 1.5 : 0 }}
                action={
                  <Button color="inherit" size="small" onClick={retrySelectedPhase}>
                    Retry
                  </Button>
                }
              >
                {friendlyErrorMessage(contentSlice.error)}
              </Alert>
            )}

            {hasContent && (
              <ArtifactContentView phase={selectedPhase} data={contentSlice.data} />
            )}

            {contentSlice?.status === "succeeded" && !hasContent && !contentLoading && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                Artifact is empty.
              </Typography>
            )}

            {contentLoading && hasContent && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Refreshing…
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const StackHeader = ({ onRefresh, refreshing }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5, gap: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: FILTER_FONT_SIZE, lineHeight: 1.45 }}>
      Worker-captured output from each remediation phase — preview, execution, and AWS API calls.
    </Typography>
    <Button
      size="small"
      startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
      onClick={onRefresh}
      disabled={refreshing}
      sx={{ textTransform: "none", fontSize: 12, flexShrink: 0 }}
    >
      Refresh
    </Button>
  </Box>
);

export default ArtifactsPanel;
